import React from "react";
import EbDialog from "../../ExecutionBlueprint/ExecutionBlueprintLiveReview/_shared/EbDialog";
import "../../ExecutionBlueprint/ExecutionBlueprintReview/InfoDialog.scss";

// ═════════════════════════════════════════════════════════════════════════════
// "How this review works" — the explainer behind the header icon on the coach's
// Annual OKR review screen, and auto-opened the first time a coach ever reaches
// that screen.
//
// Built on the same shared `EbDialog` as the Execution Blueprint's, reusing its
// `InfoDialog.scss` for the numbered list. Only the copy differs, because only
// the copy should: an Annual OKR review is a pass over Objectives, not WIGs,
// and it has no second mode to explain.
//
// ⚠️ NO "TWO MODES" PARAGRAPH. EXW's version opens by explaining Blueprint Mode
// and Review Mode. Annual OKR has one mode — §6.6's `okr-mode` is the same list
// of Objectives minus the review controls, so a toggle would swap the screen
// for a poorer copy of itself (see `AnnualOkrReview.js`). Saying "you've got
// two modes" would be describing a control that isn't there.
//
// One action and no cancel: this explains, it does not ask. The shell still
// renders a close button, because a dialog you can only leave through one
// specific button is one people feel trapped in.
// ═════════════════════════════════════════════════════════════════════════════

// Full sentences, punctuated — they read as instructions, not terse labels.
const STEPS = [
  "Work down the objectives one at a time.",
  // JK copy review O11, reworked around SMART for the client ticket "[FE]
  // Rewrite coach instructions copy around SMART (coach accepts the OKR if it
  // is SMART)" (2026-10-08). SMART is spelled out rather than left as an
  // acronym so it reads as a checklist. JK asked for the key-results check to
  // say "a clear measure AND a target built in" — a measure alone ("grow
  // revenue") isn't SMART until it says how far. The one-owner check from O11
  // stays. The next step makes SMART the accept rule and gives the coach a
  // concrete reason to flag one.
  "For each objective, check it is SMART: Specific, Measurable, Achievable, Relevant and Time-bound. Each key result should have a clear measure with a target built in, and the objective should have one owner.",
  "Accept an objective if it is SMART. If it isn't, flag it to discuss so you can work it through with the CEO.",
  "Leave a comment on any objective the CEO should read before you meet.",
  "Optionally, leave an overall comment on the OKRs as a whole.",
  "Complete the review and send it to the CEO once every objective has a decision.",
];

const ReviewInfoModal = ({ onClose }) => (
  <EbDialog
    isOpen
    size="lg"
    // Neutral, not primary: nothing here is being confirmed, and a teal chip
    // would give an explainer the weight of a decision.
    tone="neutral"
    title="Start Annual OKR review"
    subtitle="The CEO has sent you their draft Annual OKRs. Here's how to work through them."
    onClose={onClose}
    actions={[
      {
        // Present tense: "I understood" reports on some earlier occasion,
        // where this button is the coach acknowledging what they just read.
        label: "Got it",
        variant: "primary",
        onClick: onClose,
        autoFocus: true,
      },
    ]}
  >
    <p>
      Each objective carries its own key results, and your decision on it is
      what the CEO sees when the review lands.
    </p>
    <p>
      The summary on the right fills in as you go, so you can see at a glance
      what you have flagged and what you have commented on.
    </p>

    <ol className="info-steps">
      {STEPS.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  </EbDialog>
);

export default ReviewInfoModal;
